import { debug } from './utils';

export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : null;
}

export function rgbToHex(r: number, g: number, b: number): string {
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

export function invertColor(color: string): string {
  if (!window.matchMedia('(prefers-color-scheme: dark)').matches) {
    return color;
  }

  if (!color) {
    return color;
  }

  // Skip transparent colors
  if (color === 'transparent') {
    return 'color';
  }

  // Check for Hexadecimal colors (including short hex like #fff) or #f6f7f8
  if (color.startsWith('#')) {
    return invertHexColor(color);
  }

  // Check for RGB and RGBA colors
  if (color.startsWith('rgb')) {
    return invertRgbColor(color);
  }

  // Check for HSL and HSLA colors
  if (color.startsWith('hsl')) {
    return invertHslColor(color);
  }

  // Check for named colors
  if (isNamedColor(color)) {
    return invertNamedColor(color);
  }

  return color;
}

function invertHexColor(hex: string): string {
  if (hex.length < 7) {
    hex = '#' + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]; // Convert #abc to #aabbcc
  }
  const r = (255 - parseInt(hex.slice(1, 3), 16)).toString(16).padStart(2, '0');
  const g = (255 - parseInt(hex.slice(3, 5), 16)).toString(16).padStart(2, '0');
  const b = (255 - parseInt(hex.slice(5, 7), 16)).toString(16).padStart(2, '0');
  return `#${r}${g}${b}`;
}

function invertRgbColor(rgb: string): string {
  const match = rgb.match(/\d+/g)?.map(Number);
  if (!match) {
    return rgb;
  }
  const [r, g, b] = match;
  return `rgb(${255 - r}, ${255 - g}, ${255 - b})`;
}

function invertHslColor(hsl: string): string {
  const match = hsl.match(/\d+\.?\d*/g)?.map(Number);
  if (!match) {
    return hsl;
  }
  const [h, s, l] = match;
  return `hsl(${h}, ${s}%, ${100 - l}%)`;
}

function isNamedColor(color: string): boolean {
  // This function needs to check against a list of named colors.
  // For simplicity, let's assume it's a known named color.
  return /^[a-z]+$/i.test(color);
}

function invertNamedColor(color: string): string {
  // This requires converting the named color to RGB, inverting it, and converting back.
  // This is a placeholder and would need a full mapping of color names to RGB values.
  // Assuming the function `namedColorToRgb` exists to convert named colors to RGB:
  return invertRgbColor(namedColorToRgb(color));
}

function namedColorToRgb(color: string): string {
  // Convert named colors to their RGB representation.
  // This is a placeholder. A full implementation would need a mapping from color names to RGB.
  // Assuming 'red' is passed, it returns its RGB representation:
  if (color.toLowerCase() === 'red') {
    return 'rgb(255, 0, 0)';
  }
  return 'rgb(0, 0, 0)'; // Default for unknown colors
}

export function transformElement(element: Element) {
  if (element instanceof HTMLStyleElement) {
    // Check if the border color is set via any valid css property
    let styleTextContent = element.textContent;

    if (styleTextContent) {
      // Remove any prefer-color-scheme media queries
      styleTextContent = styleTextContent.replaceAll(/@media\s*\(prefers-color-scheme:\s*(dark|light)\)\s*\{([^}]+)\}/g, '');

      // Match border-color: #000000; or border-color: #000000 !important;
      const borderColor = styleTextContent.matchAll(/border-color:\s*([^;]+)/g);
      if (borderColor) {
        for (const match of borderColor) {
          // Split border-color into it's part
          const [fullMatch, color] = match;

          // Regex escaped fullMatch
          const escapedFullMatch = fullMatch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

          // Replace the colour in the fullMatch
          styleTextContent = styleTextContent.replaceAll(escapedFullMatch, fullMatch.replace(color, invertColor(color)));
        }
      }

      // Match border: 1px solid #000000; or border: 1px solid #000000 !important;
      const border = styleTextContent.matchAll(/border:\s*([^;]+)/g);
      if (border) {
        for (const match of border) {
          // Get the full match
          const fullMatch = match[0].trim();

          // Regex escaped fullMatch
          const escapedFullMatch = fullMatch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

          // Split the border property into its parts deconstructing the array
          const [_width, _style, color] = match[1].split(' ');

          // Replace the colour in the fullMatch
          styleTextContent = styleTextContent.replaceAll(escapedFullMatch, fullMatch.replace(color, invertColor(color)));
        }
      }

      // Match background-color: #000000; or background-color: #000000 !important; but nothing after the ; or ;
      const backgroundColor = styleTextContent.matchAll(/background-color:\s*([^;]+)/g);
      if (backgroundColor) {
        for (const match of backgroundColor) {
          // Split border-color into it's part
          const [fullMatch, color] = match;

          // Regex escaped fullMatch
          const escapedFullMatch = fullMatch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

          // Replace the colour in the fullMatch
          styleTextContent = styleTextContent.replaceAll(escapedFullMatch, fullMatch.replace(color, invertColor(color)));

          debug('backgroundColor', { fullMatch, replace: fullMatch.replace(color, invertColor(color)), color, invertColor: invertColor(color) });
        }
      }

      // Match background: #000000; or background: #000000 !important;
      const background = styleTextContent.matchAll(/background:\s*([^;]+)/g);
      if (background) {
        for (const match of background) {
          // Get the full match
          const fullMatch = match[0].trim();

          // Regex escaped fullMatch
          const escapedFullMatch = fullMatch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

          // Split the border property into its parts deconstructing the array
          const [_width, _style, color] = match[1].split(' ');

          // Replace the colour in the fullMatch
          styleTextContent = styleTextContent.replaceAll(escapedFullMatch, fullMatch.replace(color, invertColor(color)));
        }
      }

      // Match color: #000000; or color: #000000 !important;
      const color = styleTextContent.matchAll(/color:\s*([^;]+)/g);
      if (color) {
        for (const match of color) {
          // Split border-color into it's part
          const [fullMatch, color] = match;

          // Regex escaped fullMatch
          const escapedFullMatch = fullMatch.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

          // Replace the colour in the fullMatch
          styleTextContent = styleTextContent.replaceAll(escapedFullMatch, fullMatch.replace(color, invertColor(color)));
        }
      }

      element.textContent = styleTextContent;
    }
  }

  if (element instanceof HTMLElement) {
    const computedStyle = window.getComputedStyle(element);
    const color = computedStyle.color || element.style.color;
    const backgroundColor = computedStyle.backgroundColor || element.style.backgroundColor;
    const borderColor = computedStyle.borderColor || element.style.borderColor;

    if (color && !backgroundColor) {
      element.style.color = '';
    } else if (color && backgroundColor) {
      element.style.color = invertColor(color);
      element.style.backgroundColor = invertColor(backgroundColor);
      debug('element', element);
      debug('computedStyle.color', color);
      debug('computedStyle.backgroundColor', backgroundColor);
      debug('invertColor(color)', invertColor(color));
      debug('invertColor(backgroundColor)', invertColor(backgroundColor));
    } else if (!color && backgroundColor) {
      // Remove the background color if there is no text color
      element.style.backgroundColor = '';
    }

    if (borderColor) {
      debug('borderColor', borderColor);
      element.style.borderColor = invertColor(borderColor);
    }

    if (element.hasAttribute('bgcolor')) {
      element.removeAttribute('bgcolor');
    }
  }
}

export function processStyles(root: HTMLElement): void {
  // Transform the root element
  transformElement(root);

  const elements = root.querySelectorAll('*');
  elements.forEach(element => transformElement(element));
}
